import { z } from 'zod';
import { ulidSchema } from './id';
import { publicProfileSchema } from './user';

/**
 * Workspace join requests — the contract layer for **B38**, `BE-38.4`.
 *
 * `FR-PERM-022`: somebody who is not a member asks to join a workspace, and an
 * admin approves or declines. Four routes, on
 * `apps/server/src/routes/workspace-join-requests.ts`:
 *
 * ```
 * POST /workspaces/:id/join-requests                     → 201 | 200  requireUser
 * GET  /workspaces/:id/join-requests                     → 200        requireWorkspaceAdmin
 * POST /workspaces/:id/join-requests/:requestId/approve  → 200        requireWorkspaceAdmin
 * POST /workspaces/:id/join-requests/:requestId/decline  → 200        requireWorkspaceAdmin
 * ```
 *
 * `workspace_join_requests` has existed since `db/postgres/migrations/0001_
 * identity_and_workspaces.sql` and has sat unwired ever since. **B38 adds no
 * table and no column.**
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ `pending_user_key` IS A GENERATED STORED COLUMN ON POSTGRES, AND IT IS
 * OFF THE WIRE FOR TWO INDEPENDENT REASONS
 * ---------------------------------------------------------------------------
 * Measured against the live `molt_v1`, not read out of prose:
 *
 * ```
 * pending_user_key char(26) COLLATE "C"
 *   GENERATED ALWAYS AS (CASE WHEN status = 'pending' THEN user_id END) STORED
 * uq_workspace_join_requests_workspace_id_pending_user_key
 *   UNIQUE (workspace_id, pending_user_key)
 * ```
 *
 * It is a partial unique index expressed portably: Postgres allows unlimited
 * `NULL`s in a unique index, so a user may accumulate any number of **resolved**
 * requests and **at most one pending** — which is the whole design, and which is
 * what makes "one pending request per person per workspace" true without a
 * single line of application code.
 *
 * ⚠ **This diverges from the block's own design brief, which fixed
 * `pending_user_key` as "the application's job" — INSERT writing both columns,
 * approve and decline each setting `pending_user_key = NULL` in the same
 * `UPDATE`.** On MySQL that column is a `VIRTUAL` generated column and on
 * Postgres it is `STORED`; **on both engines the database computes it**, and an
 * `INSERT` or `UPDATE` naming it fails outright — measured against a live
 * Postgres 18 in `molt_v1_probe`: `ERROR: 428C9: cannot insert a non-DEFAULT
 * value into column "…" / DETAIL: Column "…" is a generated column.` The
 * brief's *invariant* is exactly right and
 * is what the generation expression encodes — the key equals `user_id` while and
 * only while `status = 'pending'` — but the mechanism is the schema's, not a
 * handler's, so the failure mode the brief warns about ("setting `status`
 * without nulling the key is silent and permanent") **cannot occur here**: an
 * `UPDATE` that moves `status` off `'pending'` recomputes the key to `NULL` in
 * the same statement, by construction. Assert it anyway; the assertion is cheap
 * and it is the assertion that would catch the column being replaced by a plain
 * one.
 *
 * It is off the wire regardless, and it would be off the wire even if a handler
 * did write it: it is a **storage mechanism for a uniqueness rule**, not a fact
 * about the request, it is `null` on exactly the rows a client would find it
 * interesting on, and a client binding to it would be binding to an invariant it
 * cannot see the other half of. `status` is the readable form of the same fact.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ THIS FILE'S ONE SHAPE NESTS `publicProfileSchema` — IT DOES NOT EXTEND IT,
 * AND THE TWO MEMBER SCHEMAS' PATTERN WOULD BE A BUG HERE
 * ---------------------------------------------------------------------------
 * `boardMemberSchema` and `workspaceMemberSchema` are both
 * `publicProfileSchema.extend({ … })`, and both are right to be: on a membership
 * row there **is** no id but the member's own — `board_members`' primary key is
 * `(board_id, user_id)` and `workspace_members`' is `(workspace_id, user_id)`,
 * with no surrogate key anywhere — so the profile's `id` *is* the row's
 * identity, and it is what `PATCH …/members/:userId/role` is addressed by.
 *
 * A join request is the opposite: `workspace_join_requests` has its **own**
 * `id` (`PRIMARY KEY (id)`), and `POST …/join-requests/:requestId/approve` is
 * addressed by **that** id, not by the requester's. Extending the profile here
 * would collapse two different identifiers into one field, and the failure is
 * silent in the worst way — every value is a well-formed ULID, so a client that
 * approved `request.id` would send a **user** id into a path expecting a
 * **request** id, and get a 404 for a request that plainly exists on the screen
 * in front of them. **Nest it.**
 *
 * ---------------------------------------------------------------------------
 * WHAT IS DELIBERATELY ABSENT
 * ---------------------------------------------------------------------------
 *   - **A decline `reason`, on the body or on the row.** `FR-PERM-022` is
 *     explicit: "declining does not disclose a reason to the requester." There
 *     is no column, no field and no request body — `POST …/decline` is bodiless,
 *     the whole gesture being in the path and the method. A field added "so the
 *     admin can note why" would be a field a later block is asked to surface,
 *     and the requirement forbids surfacing it.
 *   - **`workspaceId`.** All four routes are under `/workspaces/:id/`, so the
 *     path already names it; a list of rows each repeating one id is noise the
 *     caller supplied. This is the opposite call from
 *     `boardCommentingSchema`'s and `boardMemberPermissionSchema`'s `boardId`,
 *     and the distinction is real: those are *settings of a board*, one object a
 *     client caches by board id; this is a row addressed by its own id inside a
 *     collection the path already scopes.
 *   - **`updatedAt`, and any "resolved at" or "resolved by".** No acceptance
 *     criterion asks when or by whom a request was resolved, the trigger owns
 *     `updated_at`, and a field on the wire is a field a client may bind to and
 *     a later block may not remove (`./workspace`'s "No `createdAt`" note is the
 *     precedent this file's `createdAt` is argued against, below).
 *   - **The requester's email address.** `publicProfileSchema` is this
 *     repository's one definition of "the portions of a profile another member
 *     may read" (`FR-PERM-002`), and `email` is not in it — so nesting it means
 *     an email cannot arrive here by accident. That matters more on this shape
 *     than on a member list: the subject is somebody who is **not** in the
 *     workspace, so the admin reading this list has no existing relationship
 *     that would justify a wider projection.
 *   - **Anything naming a board.** `FR-PERM-022`'s edge case — "the request
 *     surface must not reveal the existence of private boards inside the
 *     workspace" — is met **by construction**: these routes return request rows
 *     and never enumerate boards. Recorded so that nobody "helpfully" adds a
 *     "boards they would gain access to" projection.
 *   - **A workspace-scoped activity event.** `workspace.join_requested`,
 *     `workspace.join_request_approved` and `workspace.join_request_declined`
 *     were checked against the live `ck_activity_entries_type` and **declined**,
 *     not minted: `publishEvents` fans out on `board_id` alone so a
 *     workspace-scoped name reaches no socket ever, a workspace-scoped entry
 *     needs `ck_activity_entries_scope` widened as well (two constraints, not
 *     one), and `FR-PERM-022`'s four acceptance criteria do not mention a feed.
 *     Approval emits the **existing** `workspace.member_added`; a decline writes
 *     no membership and `status` is itself the record. See `./events`.
 *
 * This file declares no column width constant and no bound of its own —
 * `./cross-board-move`'s and `./board-archive`'s footing. Every bound below is
 * inherited with `publicProfileSchema` or is a vocabulary restating a CHECK.
 */

/**
 * `ck_workspace_join_requests_status CHECK (status IN ('pending', 'approved',
 * 'declined'))`, `workspace_join_requests.status varchar(32) NOT NULL DEFAULT
 * 'pending'`.
 *
 * Three values, in the constraint's own order — the lifecycle's order too:
 * every request starts `'pending'` and moves once, to one of the other two, and
 * never moves again. `LABEL_COLOURS`' and `COMMENTING_PERMISSIONS`' rule about
 * keeping the array in the constraint's order applies: a drift then shows up in
 * a diff rather than only in a failing INSERT.
 *
 * ⚠ **`'pending'` is the only value the unique constraint sees.**
 * `pending_user_key` is generated as `user_id` for this value and `NULL` for the
 * other two, so the three-member vocabulary and the "one pending request"
 * guarantee are one mechanism — see the header. A fourth value added to this
 * CHECK without a thought for that expression would silently be treated as
 * *resolved* by the uniqueness rule while reading as *open* to every human.
 *
 * ⚠ **There is no `'withdrawn'` and no `'cancelled'`.** `FR-PERM-022` gives the
 * requester no gesture at all after the request is made — only an admin resolves
 * one — and a value the product cannot produce would be a case every consumer
 * carries and nothing ever fires.
 */
export const WORKSPACE_JOIN_REQUEST_STATUSES = [
  'pending',
  'approved',
  'declined',
] as const;

/**
 * `z.enum` over the vocabulary, used on the **view**, so an unknown value is a
 * parse failure rather than a string passed through.
 * `commentingPermissionSchema`'s call for the same class of column: a fourth
 * status cannot exist because the CHECK refuses it at INSERT, so a value that
 * fails to parse means the constraint and this tuple have drifted, which a read
 * should surface.
 *
 * ⚠ **There is no matching input schema, and that is the whole of the write
 * side's shape.** A status is never sent by a caller: `POST …/approve` and
 * `POST …/decline` are **bodiless**, the gesture being wholly in the path and
 * the method, so there is no `{ status }` body to declare and there must not be
 * one. A patchable status would let an admin move a resolved request back to
 * `'pending'` — reopening the unique constraint against a user who may since
 * have made another request, and turning a `23505` into the caller's problem.
 * Two routes, two verbs, no vocabulary on the way in.
 */
export const workspaceJoinRequestStatusSchema = z.enum(WORKSPACE_JOIN_REQUEST_STATUSES);

export type WorkspaceJoinRequestStatus = z.infer<typeof workspaceJoinRequestStatusSchema>;

/**
 * One join request, as `GET /workspaces/:id/join-requests` lists them and as
 * `POST /workspaces/:id/join-requests` and the two resolution routes each
 * return one.
 *
 * `FR-PERM-022` AC-level facts and nothing else: **who** is asking, **what
 * state** the request is in, and **when** they asked.
 *
 * ⚠ **The list is `requireWorkspaceAdmin`**, which is why the requester's
 * profile is nested at all — an admin cannot act on "somebody wants in" without
 * being told who. The requester themselves never reads this shape through the
 * list route; `POST` hands them back their own row, which is the same shape
 * because it describes the same thing.
 */
export const workspaceJoinRequestSchema = z.object({
  /**
   * `workspace_join_requests.id` — the **request's** id, not the requester's.
   *
   * ⚠ This is what `POST /workspaces/:id/join-requests/:requestId/approve` and
   * `…/decline` are addressed by. See the header for why this shape nests the
   * profile rather than extending it: both ids are well-formed ULIDs, so
   * confusing them produces a 404 for a row that visibly exists and no type
   * error anywhere.
   */
  id: ulidSchema,
  /**
   * `workspace_join_requests.status`.
   *
   * On the wire on all four routes, including the two that have just set it,
   * because a resolution route answering with the row it resolved is how a
   * client reconciles an optimistic update — and because `POST
   * /workspaces/:id/join-requests` may answer with an **existing** `'pending'`
   * row rather than a newly inserted one (see the file header's uniqueness
   * note, and `FR-PERM-022`: a second request while one is pending is not a
   * second row and not a `23505`), so the caller cannot infer the state from
   * the fact that their request succeeded.
   */
  status: workspaceJoinRequestStatusSchema,
  /**
   * The person asking — `publicProfileSchema`, **nested**, not extended.
   *
   * The header argues the nesting at length; the short form is that this row
   * has an id of its own and the profile's `id` is a different id addressing a
   * different thing.
   *
   * `publicProfileSchema` is this repository's one definition of what one
   * account may read about another (`FR-PERM-002`), so this cannot acquire an
   * `email` without somebody adding one to the base — which is the point.
   *
   * ⚠ **Not nullable.** `fk_workspace_join_requests_user_id` is `ON DELETE
   * CASCADE`, so a request cannot outlive its requester's account; there is no
   * "deleted account" state for this shape to express, unlike a comment author.
   */
  user: publicProfileSchema,
  /**
   * `workspace_join_requests.created_at`, serialised as the driver returns it.
   *
   * A bounded string rather than `z.iso.datetime()` or a coerced `Date`, for
   * the reason `workspaceMemberSchema.joinedAt` states in full: the column is
   * `timestamptz(3)` and `pg` hands it back in Postgres' own spelling
   * (`2026-08-10 12:00:00.123+00`), which is a legitimate instant and not an ISO
   * 8601 `T`/`Z` form, so pinning the serialisation here would make this schema
   * reject the value the database actually produced.
   *
   * ⚠ **On the wire although `./workspace` withholds `createdAt` from
   * `workspaceSchema`, and the contrast is the reason rather than an
   * inconsistency.** Nothing asks when a *workspace* was made. A **request**, by
   * contrast, is a queue item an admin triages: age is the ordering fact and the
   * only thing distinguishing a request made an hour ago from one that has sat
   * unanswered for a month. `boardInviteLinkSchema.createdAt` is on the wire for
   * the same class of reason and `boardSchema.closedAt` is withheld for the
   * opposite one; those three together are the test to apply before adding a
   * timestamp to any shape in this package.
   */
  createdAt: z.string().min(1),
});

export type WorkspaceJoinRequest = z.infer<typeof workspaceJoinRequestSchema>;

/**
 * What `GET /workspaces/:id/join-requests` returns.
 *
 * ⚠ **A bare array, with no cursor and no `?limit`.** That is
 * `workspaceMemberListSchema`'s and `boardMemberListSchema`'s shape rather than
 * `./comment`'s or `./activity`'s, and the distinction those files draw is
 * whether the collection is **unbounded**: comments and activity entries are,
 * and a page size ceiling is the only thing between the server and a request
 * that reads everything. Join requests are bounded by the number of people who
 * have asked to join one workspace and not been answered, which is a queue an
 * admin is expected to empty — if it ever grows unbounded the workspace has a
 * moderation problem that a cursor would hide rather than solve. A block that
 * finds otherwise adds a querystring on `./comment`'s precedent, and adding one
 * later is additive: `z.strictObject({})` on the route makes an early `?limit`
 * a 422 rather than a silently ignored parameter.
 *
 * ⚠ **It is NOT filtered to `'pending'` by this schema, and must not be.**
 * `status` is on every row precisely so an admin surface can show the queue and
 * the history through one route; which of them a client renders is `FE-38.4`'s.
 * A route that returned only pending rows would make the other two vocabulary
 * members unreachable and `status` a field that is always `'pending'` — the
 * dead-on-arrival shape `boardSchema`'s lifecycle note rejects.
 */
export const workspaceJoinRequestListSchema = z.array(workspaceJoinRequestSchema);

export type WorkspaceJoinRequestList = z.infer<typeof workspaceJoinRequestListSchema>;
